import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { StoreRequisitionView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  decideStoreRequisition,
  issueStoreRequisition,
  storeRequisitionQuery,
} from '../api/procurement.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { LoadFailed, OfflineNote, ReasonDialog } from '../procurement/common.tsx';
import { documentLabel, documentTag } from '../procurement/format.ts';
import { errorText, newRequestKey } from '../stock/common.tsx';
import { formatQuantity, readQuantity } from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// One store requisition (docs/plans/phase2-procurement.md §5.9). The supplying store
// issues what it can, earliest expiry first; each issue is an ordinary transfer.

const route = getRouteApi('/_app/stores/$storeId/requisitions/$requisitionId');

type Decision = 'close' | 'reject' | 'cancel';
const WORDS: Record<Decision, { confirm: string; toast: string }> = {
  close: { confirm: 'Close short', toast: 'Requisition closed' },
  reject: { confirm: 'Reject requisition', toast: 'Requisition rejected' },
  cancel: { confirm: 'Cancel requisition', toast: 'Requisition cancelled' },
};

export function StoreRequisitionPage() {
  const { storeId, requisitionId } = route.useParams();
  const req = useQuery(storeRequisitionQuery(requisitionId));
  if (req.isPending) return <LoadingBlock label="Loading the requisition" rows={4} />;
  if (req.isError)
    return (
      <LoadFailed
        error={req.error}
        fallback="The store requisition couldn't be loaded."
        onRetry={() => void req.refetch()}
      />
    );
  return <Detail storeId={storeId} req={req.data} />;
}

function Detail({ storeId, req }: { storeId: string; req: StoreRequisitionView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const open = req.status === 'open' || req.status === 'partly_issued';
  const supplying = storeId === req.supplyingStore.id;
  const mayIssue = open && supplying && session !== null && can(session, 'stock.transfer');
  const mine = session?.user.id === req.requestedBy.id;
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      req.lines.map((l) => [l.itemId, l.outstanding === 0 ? '' : String(l.outstanding)]),
    ),
  );
  const [issueKey, setIssueKey] = useState(newRequestKey);
  const [deciding, setDeciding] = useState<Decision | null>(null);

  const refresh = async (result: StoreRequisitionView) => {
    queryClient.setQueryData(storeRequisitionQuery(req.id).queryKey, result);
    await queryClient.invalidateQueries({ queryKey: ['stock'] });
  };
  const issuing = req.lines
    .map((l) => ({ itemId: l.itemId, quantity: readQuantity(quantities[l.itemId] ?? '') ?? 0 }))
    .filter((l) => l.quantity > 0);
  const issue = useMutation({
    mutationFn: () => issueStoreRequisition(req.id, { clientRequestId: issueKey, lines: issuing }),
    onSuccess: async (result) => {
      await refresh(result);
      setIssueKey(newRequestKey());
      setQuantities(
        Object.fromEntries(
          result.lines.map((l) => [l.itemId, l.outstanding === 0 ? '' : String(l.outstanding)]),
        ),
      );
      toasts.show({ title: 'Stock issued', description: result.number });
    },
  });
  const decide = useMutation({
    mutationFn: ({ decision, reason }: { decision: Decision; reason: string }) =>
      decideStoreRequisition(req.id, decision, reason),
    onSuccess: async (result, { decision }) => {
      await refresh(result);
      toasts.show({ title: WORDS[decision].toast, description: result.number });
      setDeciding(null);
    },
  });

  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/stores/$storeId/requisitions" params={{ storeId }}>
          Back to store requisitions
        </Link>
      </p>
      <PageHeader title={`Store requisition ${req.number}`} />
      <Section
        title={`${req.requestingStore.name} asks ${req.supplyingStore.name}`}
        actions={<Tag state={documentTag(req.status)}>{documentLabel(req.status)}</Tag>}
      >
        <dl className={styles.facts}>
          <dt>Requisition</dt>
          <dd>
            <span className="wodi-id">{req.number}</span>
          </dd>
          <dt>Asked by</dt>
          <dd>
            {req.requestedBy.name}, {formatInstant(req.requestedAt)}
          </dd>
          {req.note === null ? null : (
            <>
              <dt>Note</dt>
              <dd>{req.note}</dd>
            </>
          )}
          {req.closeReason === null ? null : (
            <>
              <dt>{documentLabel(req.status)}</dt>
              <dd>
                {req.closedBy?.name ?? ''}: {req.closeReason}
              </dd>
            </>
          )}
          {req.issues.length === 0 ? null : (
            <>
              <dt>Issued on</dt>
              <dd>
                {req.issues.map((i, n) => (
                  <span key={i.transferId}>
                    {n === 0 ? '' : ', '}
                    <span className="wodi-id">{i.number}</span>
                  </span>
                ))}
              </dd>
            </>
          )}
        </dl>
      </Section>
      <Section title="Lines">
        <TableScroll>
          <Table flat aria-label={`Lines of ${req.number}`}>
            <TableHeader>
              <Column isRowHeader>Item</Column>
              <Column numeric>Asked for</Column>
              <Column numeric>Issued</Column>
              <Column numeric>Still to issue</Column>
            </TableHeader>
            <TableBody items={req.lines.map((l) => ({ ...l, id: l.itemId }))}>
              {(l) => (
                <Row id={l.itemId}>
                  <Cell>{l.itemName}</Cell>
                  <Cell numeric>{formatQuantity(l.requested, l.baseUnitDisplay)}</Cell>
                  <Cell numeric>{formatQuantity(l.issued, l.baseUnitDisplay)}</Cell>
                  <Cell numeric>{formatQuantity(l.outstanding, l.baseUnitDisplay)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      {mayIssue ? (
        <Section title={`Issue from ${req.supplyingStore.name}`}>
          <p className={styles.meta}>
            Batches that expire first go first. Issue what you have; close the rest short if it
            won't come.
          </p>
          {req.lines
            .filter((l) => l.outstanding > 0)
            .map((l) => (
              <TextField
                key={l.itemId}
                label={`${l.itemName}: issue now (${l.baseUnitDisplay})`}
                inputMode="numeric"
                value={quantities[l.itemId] ?? ''}
                onChange={(v) => {
                  setQuantities((q) => ({ ...q, [l.itemId]: v }));
                }}
              />
            ))}
          {issue.error === null ? null : (
            <Notice tone="error">{errorText(issue.error, 'Nothing was issued. Try again.')}</Notice>
          )}
          <div className={styles.actions}>
            <Button
              variant="primary"
              isDisabled={!online || issuing.length === 0}
              isPending={issue.isPending}
              pendingLabel="Issuing…"
              onPress={() => {
                issue.mutate();
              }}
            >
              Issue stock
            </Button>
            {req.status === 'partly_issued' ? (
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setDeciding('close');
                }}
              >
                Close short
              </Button>
            ) : (
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setDeciding('reject');
                }}
              >
                Reject requisition
              </Button>
            )}
          </div>
        </Section>
      ) : null}
      {open && !supplying && mine && req.status === 'open' ? (
        <div className={styles.actions}>
          <Button
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              setDeciding('cancel');
            }}
          >
            Cancel requisition
          </Button>
        </div>
      ) : null}
      <OfflineNote what="Issuing stock" />
      {deciding === null ? null : (
        <ReasonDialog
          title={`${WORDS[deciding].confirm}: ${req.number}`}
          confirmLabel={WORDS[deciding].confirm}
          isPending={decide.isPending}
          error={decide.error}
          onConfirm={(reason) => {
            decide.mutate({ decision: deciding, reason });
          }}
          onClose={() => {
            setDeciding(null);
            decide.reset();
          }}
        />
      )}
    </div>
  );
}
